import { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, Activity } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import { backdropClose } from '../utils/backdropClose';
import { fmtTarget, type TargetMap } from '../app/member/plan/planShared';

/* Trainer-side view of what the member actually did — the read side of the
   session player. The plan panel next door says what they were asked to do;
   this says what happened, set by set, including why a set was cut short.

   Read-only on purpose: a coach changes the future through the plan, never by
   editing history. */

interface Props {
  memberId: string;
  memberName: string;
  onClose: () => void;
}

interface SessionRow {
  id: string;
  date: string;
  focus: string;
  status: string;
  endedAt: string | null;
  totalSec: number;
  setsLogged: number;
  exercisesDone: number;
  exercisesTotal: number;
}

interface SetRow {
  setIndex: number;
  target: TargetMap;
  actual: TargetMap;
  reason: string | null;
  restSecAfter: number | null;
  loggedAt: string;
}

interface DetailItem {
  id: string;
  exerciseName: string;
  workoutType: string;
  target: TargetMap;
  status: string;
  setsCompleted: number;
  planNote: string | null;
  sets: SetRow[];
}

interface SessionDetail {
  id: string;
  date: string;
  focus: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  workSec: number;
  restSec: number;
  items: DetailItem[];
}

/** Why a set fell short, in the words a coach would use. */
const REASON_LABEL: Record<string, string> = {
  failed: 'Failed the rep',
  form: 'Form broke down',
  easy: 'Too easy',
  assisted: 'Needed a spot',
};

const STATUS_LABEL: Record<string, { text: string; className: string }> = {
  complete: { text: 'Finished', className: 'bg-lime-100 text-lime-800' },
  ended_early: { text: 'Ended early', className: 'bg-amber-100 text-amber-800' },
  abandoned: { text: 'Abandoned', className: 'bg-gray-100 text-gray-600' },
};

async function authFetch(path: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed (${res.status})`) as Error & { code?: string };
    err.code = body?.code;
    throw err;
  }
  return body;
}

function fmtDuration(totalSec: number): string {
  if (!totalSec) return '—';
  const m = Math.round(totalSec / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

/** Last 90 days, which is as far back as a review conversation ever reaches. */
function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 90 * 86400000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

export default function MemberWorkoutLogPanel({ memberId, memberName, onClose }: Props) {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { from, to } = defaultRange();
        const body = await authFetch(`/api/members/${memberId}/sessions?from=${from}&to=${to}`);
        if (cancelled) return;
        // Newest first reads better here: a coach opens this to see the last
        // session, not the first one of the quarter.
        setSessions((body.data as SessionRow[]).slice().reverse());
      } catch (err) {
        if (cancelled) return;
        if ((err as { code?: string }).code === 'PROGRESS_SHARING_DISABLED') setBlocked(true);
        else setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId]);

  const openSession = useCallback(async (id: string) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const body = await authFetch(`/api/members/${memberId}/sessions/${id}`);
      setDetail(body.data as SessionDetail);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingDetail(false);
    }
  }, [memberId]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      {...backdropClose(onClose)}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            {detail && (
              <button
                onClick={() => setDetail(null)}
                className="p-2 -ml-2 rounded-lg hover:bg-gray-100"
                aria-label="Back to sessions"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {detail ? detail.focus : 'Workouts'}
              </h2>
              <p className="text-sm text-gray-500 truncate">
                {detail ? fmtDate(detail.date) : memberName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {blocked ? (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
              <strong>Progress sharing is off.</strong> {memberName} has turned off sharing their progress
              with coaches, so their workouts are not visible here. They can turn it back on in the member
              app under Profile &rarr; Notifications &amp; privacy.
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">{error}</div>
          ) : loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : detail ? (
            <SessionDetailView detail={detail} />
          ) : sessions.length === 0 ? (
            <div className="py-12 text-center">
              <Activity className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No workouts logged in the last 90 days.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {loadingDetail && (
                <div className="h-1 bg-lime-200 rounded-full overflow-hidden">
                  <div className="h-full w-1/3 bg-lime-500 animate-pulse" />
                </div>
              )}
              {sessions.map(s => {
                const badge = STATUS_LABEL[s.status] ?? STATUS_LABEL.complete;
                return (
                  <button
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className="w-full text-left p-4 rounded-xl border border-gray-100 hover:border-lime-200 hover:bg-lime-50/40 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 truncate">{s.focus}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}>
                            {badge.text}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {fmtDate(s.date)} · {s.exercisesDone}/{s.exercisesTotal} exercises · {s.setsLogged} sets
                        </p>
                      </div>
                      <span className="text-sm text-gray-500 tabular-nums shrink-0">
                        {fmtDuration(s.totalSec)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionDetailView({ detail }: { detail: SessionDetail }) {
  const badge = STATUS_LABEL[detail.status] ?? STATUS_LABEL.complete;
  const total = detail.workSec + detail.restSec;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}>
          {badge.text}
        </span>
        {/* A swept session never got the work/rest write, so it reads as a dash
            rather than a wall-clock guess that includes time in a locker. */}
        <span className="text-gray-500">{total ? fmtDuration(total) : 'Duration not recorded'}</span>
      </div>

      {detail.items.map(item => (
        <div key={item.id} className="rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{item.exerciseName}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Target {fmtTarget(item.target) || '—'}
                {item.planNote ? ` · ${item.planNote}` : ''}
              </p>
            </div>
            {item.status === 'skipped' && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 shrink-0">
                Skipped
              </span>
            )}
          </div>

          {item.sets.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400">No sets logged.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-2 font-medium">Set</th>
                  <th className="px-4 py-2 font-medium">Target</th>
                  <th className="px-4 py-2 font-medium">Actual</th>
                  <th className="px-4 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {item.sets.map(s => (
                  <tr key={s.setIndex} className="border-t border-gray-50">
                    <td className="px-4 py-2 text-gray-500 tabular-nums">{s.setIndex + 1}</td>
                    <td className="px-4 py-2 text-gray-500 tabular-nums">{fmtTarget(s.target) || '—'}</td>
                    <td className="px-4 py-2 text-gray-900 font-medium tabular-nums">
                      {fmtTarget(s.actual) || '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {s.reason ? (REASON_LABEL[s.reason] ?? s.reason) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}
